import Check from '@mui/icons-material/CheckSharp';
// import CancelIcon from "@mui/icons-material/Cancel";
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownSharpIcon from '@mui/icons-material/KeyboardArrowDownSharp';
import React, { useEffect, useRef, useState } from 'react';

import AlertIcon from '../../../assets/icons/AlertIcon';
import { useRandomIds } from '../../../helpers/shared';
import colors from '../../../styles/tokens/colors';
import { Container, ErrorBlock, Overlay } from './styles';

/**
 * Componente de dropdown que exibe uma lista de opções selecionáveis.
 * @param {object} props - Propriedades do componente.
 * @param {object} props.data - Objeto contendo dados do dropdown.
 * @param {string} props.data.label - Rótulo do dropdown.
 * @param {string} props.data.placeholder - Placeholder do dropdown.
 * @param {Array<{ value: string, text: string }>} props.data.items - Lista de opções do dropdown, contendo objetos com valores e textos.
 * @param {boolean} [props.isDisabled=false] - Indica se o dropdown está desabilitado.
 * @param {boolean} props.isError - Indica se ocorreu um erro no dropdown.
 * @param {string} props.selectedValue - Valor da opção selecionada.
 * @param {(option: { value: string, text: string }) => void} props.onSelectOption - Função de callback para quando uma opção é selecionada.
 * @param {() => void} [props.onOpenSelect=null] - Função de callback para quando o dropdown é aberto.
 * @param {boolean} props.isSelectActive - Indica se o dropdown está ativo.
 * @param {boolean} [props.large=false] - Indica se o dropdown é grande.
 * @param {boolean} [props.isOverflowed=false] - Indica se o dropdown está com overflow.
 * @returns {JSX.Element} O componente de dropdown.
 */
export function Dropdown({
	data,
	isDisabled = false,
	isError,
	selectedValue,
	onSelectOption,
	onOpenSelect = null,
	isSelectActive,
	large = false,
	isOverflowed = false,
}) {
	const id = useRandomIds();

	const container = useRef(null);
	const [active, setActive] = useState(false);

	const toggleDropdown = () => setActive(!active);

	useEffect(() => {
		const handleClickOutside = (e) => {
			const target = e.target;

			if (container?.current && !container?.current.contains(target)) {
				setActive(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [container]);

	return (
		<>
			<Container large={large} ref={container}>
				<label id={id('label')} htmlFor={id('dropdown')} large={large}>
					{data.label}
				</label>
				<button
					className={
						(isSelectActive && 'actived') ||
            (isDisabled && 'disabled') ||
            (isError && !selectedValue && 'error') ||
            (selectedValue !== '' && 'fill')
					}
					type="button"
					onClick={!isDisabled ? onOpenSelect || toggleDropdown : () => {}}
					role="combobox" /* select */
					tabIndex={0}
					aria-controls={id('options')} /* controla os elementos filhos - <span> */
					aria-owns={id('options')}
					aria-expanded={isSelectActive}
					aria-haspopup="listbox"
					aria-required={true}
					aria-invalid={isError && !selectedValue && !isSelectActive}
					id={id('dropdown')}
				>
					{!selectedValue || isSelectActive ? (
						<p className="placeholder">{data.placeholder}</p>
					) : (
						<p aria-labelledby={id('label')}>{selectedValue}</p>
					)}

					<span>
						{isError && !selectedValue && !isSelectActive && <ErrorIcon />}
						<KeyboardArrowDownSharpIcon size={18} titleAccess="Abre menu de opções" htmlColor="#CFCFCF" />
					</span>
				</button>
				{(isSelectActive || active) && (
					<ul className={isOverflowed ? 'overflowed' : ''} id={id('options')} aria-labelledby={id('label')} role="listbox">
						{data &&
              data.items.map((option) => (
              	<li key={option.value}>
              		<button
              			role="option"
              			tabIndex={0}
              			onClick={() => {
              				onSelectOption(option);
              				setActive(false);
              			}}
              			onKeyDown={(event) => {
              				if (['Space', 'Enter'].includes(event.code)) {
              					onSelectOption(option);
              					setActive(false);
              				}
              			}}
              			aria-selected={selectedValue === option.text}
              			id={`option-${option.value}`}
              			className={selectedValue === option.text && 'actived-option'}
              		>
              			{option.text}
              			{selectedValue === option.text && <Check size={18} />}
              		</button>
              	</li>
              ))}
					</ul>
				)}
				{isError && !selectedValue && !isSelectActive && <ErrorBlock>
					<AlertIcon width='16' height='16' style={{ color: colors.error.main }} />
					{isError}</ErrorBlock>}
			</Container>
			{isSelectActive && <Overlay onClick={onOpenSelect} />}
		</>
	);
}
